import { bexHttpClient } from '../../clients';

/**
 * Service class for performing schema operations via BEX server (port 4000).
 * Used for connection-linked schemas stored in Redis.
 */
class BexSchemaService {
    private baseURL: string;

    constructor(baseURL = '/redis/schemas') {
        this.baseURL = baseURL;
    }

    /**
     * Creates schemas on the BEX server.
     * @param organizationId - The organization ID.
     * @param appId - The application ID.
     * @param payload - The schema payload containing schemas array.
     */
    async createSchemas(
        organizationId: string,
        appId: string,
        payload: { schemas: Array<{ tableName: string; columns: any[] }> },
        connectionId?: string
    ): Promise<string> {
        const response = await bexHttpClient.post(`${this.baseURL}/create`, {
            organizationId,
            appId,
            ...(connectionId ? { connectionId } : {}),
            ...payload,
        });
        return response.data?.message || 'Schemas created successfully';
    }

    /**
     * Retrieves schemas from the BEX server.
     * @param organizationId - The organization ID.
     * @param appId - The application ID.
     * @param tableName - The table name to retrieve (use '*' for all).
     */
    async retrieveSchemas(
        tableName: string = '*',
        appId: string,
        organizationId: string,
        connectionId?: string
    ) {
        const response = await bexHttpClient.get(`${this.baseURL}/get`, {
            params: { organizationId, appId, tableName, ...(connectionId ? { connectionId } : {}) },
        });
        return response;
    }

    /**
     * Updates schemas on the BEX server.
     * @param organizationId - The organization ID.
     * @param appId - The application ID.
     * @param payload - The modification payload.
     */
    async modifySchemas(
        payload: object,
        appId: string,
        organizationId: string,
        connectionId?: string
    ): Promise<object> {
        const response = await bexHttpClient.put(`${this.baseURL}/update`, {
            organizationId,
            appId,
            ...(connectionId ? { connectionId } : {}),
            ...payload,
        });
        return response.data;
    }

    /**
     * Deletes schemas on the BEX server.
     * @param organizationId - The organization ID.
     * @param appId - The application ID.
     * @param payload - The delete payload.
     */
    async deleteSchemas(
        payload: object,
        organizationId: string,
        appId: string,
        connectionId?: string
    ) {
        const response = await bexHttpClient.delete(`${this.baseURL}/delete`, {
            data: {
                organizationId,
                appId,
                ...(connectionId ? { connectionId } : {}),
                ...payload,
            },
        });
        return response;
    }

    /**
     * Searches data records via BEX server.
     */
    async searchData(payload: {
        organizationId: string;
        appId: string;
        connectionId?: string;
        target: string;
        filters?: object;
        columnsToRetrieve?: string[];
        maxRowsPerPage?: number;
        pageToRetrieve?: number;
    }) {
        const response = await bexHttpClient.post(`${this.baseURL}/search`, payload);
        return response.data;
    }

    async createDataRecord(payload: {
        organizationId: string;
        appId: string;
        tableName: string;
        record: Record<string, any>;
    }) {
        const response = await bexHttpClient.post(`${this.baseURL}/data/create`, payload);
        return response.data;
    }

    async updateDataRecord(payload: {
        organizationId: string;
        appId: string;
        tableName: string;
        recordId: string;
        updates: Record<string, any>;
    }) {
        const response = await bexHttpClient.put(`${this.baseURL}/data/update`, payload);
        return response.data;
    }

    async deleteDataRecord(payload: {
        organizationId: string;
        appId: string;
        tableName: string;
        recordId: string;
    }) {
        const response = await bexHttpClient.delete(`${this.baseURL}/data/delete`, { data: payload });
        return response.data;
    }
}

const bexSchemaService = new BexSchemaService();
export { bexSchemaService };
